import React, { ChangeEvent, createRef, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { clearLogo, getCustomizeAsync, saveCustomizeAsync, selectCustomize, selectCustomizedItems, selectLogo, uploadLogoAsync } from './CustomizeSlice'
import { useParams } from 'react-router-dom'
import { MuiColorInput } from 'mui-color-input'
import {
  Box, Button, Card, CardContent, CircularProgress, Divider, Grid, InputAdornment,
  Paper, Stack, TextField, Typography
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SaveIcon from '@mui/icons-material/SaveOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import { getSlugsAsync, selectSlugs } from '../slugs/SlugSlice'
import { selectShowCustomizePreview } from '../auth/AuthSlice'

const CustomizeHome = () => {
  const dispatch = useAppDispatch()
  const [sort] = useState('createdTime,desc')
  const [size] = useState(50)
  const [page] = useState(0)
  const { mode } = useParams()
  const customize = useAppSelector(selectCustomize)
  const customizedItems = useAppSelector(selectCustomizedItems)
  const slugs = useAppSelector(selectSlugs)
  const showCustomizePreview = useAppSelector(selectShowCustomizePreview)
  const logo = useAppSelector(selectLogo)
  const first = customizedItems?.content?.length && customizedItems?.content?.length >= 1 ? customizedItems.content[0] : undefined
  const [primaryColor, setPrimaryColor] = useState(first?.primaryColor ?? customize?.primaryColor ?? '#2f2b7a')
  const [secondaryColor, setSecondaryColor] = useState(first?.secondaryColor ?? customize?.secondaryColor ?? '#6c63ff')
  const [tertiaryColor, setTertiaryColor] = useState(first?.tertiaryColor ?? customize?.tertiaryColor ?? '#ffffff')
  const [logoB64, setLogoB64] = useState(first?.logoB64 ?? customize?.logoB64 ?? '')
  const [logoUrl, setLogoUrl] = useState(first?.logoUrl ?? customize?.logoUrl ?? '')
  const [logoObjFit, setLogoObjFit] = useState(first?.logoObjFit ?? customize?.logoObjFit ?? 'fill')
  const [logoWidth, setLogoWidth] = useState(first?.logoWidth ?? customize?.logoWidth ?? 200)
  const [logoHeight, setLogoHeight] = useState(first?.logoHeight ?? customize?.logoHeight ?? 50)
  const [logoAvailable, setLogoAvailable] = useState((customizedItems?.content?.length && customizedItems?.content?.length >= 1) || logo?.presignedUrl ? true : false)
  const [saving, setSaving] = useState(false)
  const logoFileRef = createRef<HTMLInputElement>()
  const [iFrameRefKey] = useState(0)

  useEffect(() => {
    dispatch(getCustomizeAsync({ page: { page, size, sort }, mode: mode === 'all' ? 'all' : 'me' }))
    dispatch(getSlugsAsync({ page: { page, size, sort }, mode: 'my', arg: '' }))
  }, [])

  useEffect(() => {
    const f = customizedItems?.content?.length && customizedItems?.content?.length >= 1 ? customizedItems.content[0] : undefined
    setPrimaryColor(f?.primaryColor ?? customize?.primaryColor ?? '#2f2b7a')
    setSecondaryColor(f?.secondaryColor ?? customize?.secondaryColor ?? '#6c63ff')
    setTertiaryColor(f?.tertiaryColor ?? customize?.tertiaryColor ?? '#ffffff')
    setLogoB64(f?.logoB64 ?? customize?.logoB64 ?? '')
    setLogoUrl(f?.logoUrl ?? customize?.logoUrl ?? '')
    setLogoObjFit(f?.logoObjFit ?? customize?.logoObjFit ?? 'fill')
    setLogoWidth(f?.logoWidth ?? customize?.logoWidth ?? 200)
    setLogoHeight(f?.logoHeight ?? customize?.logoHeight ?? 50)
    setLogoAvailable((customizedItems?.content?.length && customizedItems?.content?.length >= 1) || logo?.presignedUrl ? true : false)
  }, [customizedItems, customize])

  const shownLogo = logoB64 || (first?.logoUrl) || logo?.presignedUrl || logoUrl

  const onSave = () => {
    setSaving(true)
    dispatch(saveCustomizeAsync({
      ...(customize?.slug && { slug: customize?.slug }),
      ...(first?.slug && { slug: first.slug }),
      ...(logo?.filename && { logoS3: logo?.filename }),
      primaryColor, secondaryColor, tertiaryColor, logoB64, logoWidth, logoHeight, logoObjFit
    }))
    setTimeout(() => setSaving(false), 800)
  }

  const swatch = (c: string) => <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: c, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />

  return (<Box sx={{ maxWidth: 1200, mx: 'auto', textAlign: 'left' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Report branding</Typography>
        <Typography variant="body2" color="text.secondary">Your logo and colors are applied to every report you generate.</Typography>
      </Box>
      <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} onClick={onSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </Box>

    <Grid container spacing={3}>
      {/* Controls column */}
      <Grid item xs={12} md={5}>
        <Stack spacing={3}>
          {/* Logo card */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Logo</Typography>
              <Typography variant="caption" color="text.secondary">PNG or SVG with a transparent background works best.</Typography>

              <Box sx={{
                mt: 2, mb: 2, height: 140, borderRadius: 2, border: '1px dashed', borderColor: 'divider',
                display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', overflow: 'hidden'
              }}>
                {shownLogo
                  ? <img src={shownLogo} alt="logo" style={{ maxWidth: Math.min(logoWidth, 260), maxHeight: 120, objectFit: 'contain' }} />
                  : <Stack alignItems="center" spacing={0.5} sx={{ color: 'text.disabled' }}>
                    <ImageOutlinedIcon fontSize="large" />
                    <Typography variant="caption">No logo uploaded</Typography>
                  </Stack>}
              </Box>

              <Stack direction="row" spacing={1}>
                <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={logoAvailable} sx={{ textTransform: 'none' }}>
                  Upload
                  <input type="file" accept="image/*" hidden ref={logoFileRef} onChangeCapture={(e: ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files?.length) { dispatch(uploadLogoAsync(e.target.files[0])); setLogoAvailable(true) }
                  }} />
                </Button>
                <Button variant="text" color="error" startIcon={<DeleteOutlineIcon />} disabled={!logoAvailable} sx={{ textTransform: 'none' }} onClick={() => {
                  setLogoB64(''); setLogoUrl('')
                  if (logoFileRef.current) logoFileRef.current.value = ''
                  setLogoAvailable(false); dispatch(clearLogo())
                }}>Remove</Button>
              </Stack>

              <TextField sx={{ mt: 2 }} size="small" label="Display width" value={logoWidth}
                onChange={(e) => setLogoWidth(Number(e.target.value) > 200 ? 200 : Number(e.target.value))}
                InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                helperText="Max 200px" />
            </CardContent>
          </Card>

          {/* Colors card */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Colors</Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {swatch(primaryColor)}
                  <MuiColorInput label="Primary" format="hex" value={primaryColor} onChange={setPrimaryColor} size="small" fullWidth />
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {swatch(secondaryColor)}
                  <MuiColorInput label="Secondary" format="hex" value={secondaryColor} onChange={setSecondaryColor} size="small" fullWidth />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      {/* Live preview column */}
      <Grid item xs={12} md={7}>
        <Card variant="outlined" sx={{ borderRadius: 2, position: { md: 'sticky' }, top: 16 }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Live preview</Typography>
            <Typography variant="caption" color="text.secondary">— exactly how your report renders</Typography>
          </Box>
          {showCustomizePreview && slugs?.content?.[0]?.slug
            ? <Box component="iframe"
              src={`/analysis/slug/${slugs.content[0].slug}?preview=true&primaryColor=${encodeURIComponent(primaryColor)}&secondaryColor=${encodeURIComponent(secondaryColor)}&tertiaryColor=${encodeURIComponent(tertiaryColor)}&logoB64=${logoB64}&logoWidth=${logoWidth}&logoHeight=${logoHeight}&logoObjFit=${logoObjFit}`}
              key={iFrameRefKey} title="Preview" sx={{ width: '100%', height: '78vh', border: 0, display: 'block' }} />
            : <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
              <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary', textAlign: 'center' }}>
                <ImageOutlinedIcon fontSize="large" />
                <Typography variant="body2">Generate a report first, then it appears here with your branding applied live.</Typography>
              </Stack>
            </Box>}
        </Card>
      </Grid>
    </Grid>
  </Box>)
}

export default CustomizeHome
